/**
 * AdminLeadDetailPage - Read-only lead detail view for platform admins
 * Shows lead data within the admin context (no impersonation needed)
 */

import { useState, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import { formatPrice } from '@limo/shared'
import { useToast } from '@/hooks/use-toast'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  ArrowLeft,
  AlertCircle,
  Calendar,
  Clock,
  MapPin,
  User,
  Mail,
  Phone,
  Building,
  Tag,
  FileText,
  StickyNote,
  PhoneCall,
  CalendarPlus,
  CheckCircle,
  ExternalLink,
  Building2,
  Trophy,
  ThumbsDown,
  Loader2,
  Trash2,
  CalendarClock,
  Lock,
  XCircle,
} from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {
  LeadScoreBadge,
  LeadSourceBadge,
  LeadStatusBadge,
  LeadStageBadge,
  LeadOutcomeDialog,
  LeadAgeIndicator,
} from '@/components/leads'

// Default follow-up to tomorrow at noon (local time for datetime-local input)
const getDefaultFollowUpDate = () => {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  d.setHours(12, 0, 0, 0)
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// Icon mapping for activity types
const activityIcons: Record<string, React.ElementType> = {
  note: StickyNote,
  call: PhoneCall,
  email: Mail,
  meeting: Calendar,
  stage_change: Tag,
  status_change: CheckCircle,
  assignment: User,
  follow_up: Clock,
  quote_updated: FileText,
  system: FileText,
}

// Format helpers
const formatCurrency = (amount: number | null | undefined) =>
  amount != null
    ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount)
    : '-'

const formatDateTime = (dateStr: string | null | undefined) =>
  dateStr
    ? new Date(dateStr).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '-'

const formatDate = (dateStr: string | null | undefined) =>
  dateStr
    ? new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '-'

const formatRelativeTime = (dateStr: string) => {
  const now = new Date()
  const date = new Date(dateStr)
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return formatDate(dateStr)
}

/**
 * Admin-specific hook to fetch a lead by ID without account context
 */
function useAdminLead(leadId: string | undefined) {
  return useQuery({
    queryKey: ['admin', 'lead', leadId],
    queryFn: async () => {
      if (!leadId) return null

      const { data, error } = await supabase
        .from('leads')
        .select(`
          *,
          accounts!inner (
            id,
            name,
            slug,
            status
          ),
          lead_stages (
            id,
            name,
            color,
            position,
            is_default,
            is_won,
            is_lost
          ),
          profiles:assigned_to (
            id,
            full_name,
            email
          ),
          booking_quotes:quote_id (
            id,
            token,
            status,
            total_amount
          )
        `)
        .eq('id', leadId)
        .single()

      if (error) throw error

      return {
        id: data.id,
        accountId: data.account_id,
        accountName: (data.accounts as any)?.name || 'Unknown',
        accountSlug: (data.accounts as any)?.slug || '',
        accountStatus: (data.accounts as any)?.status || 'unknown',
        quoteId: data.quote_id,
        stageId: data.stage_id,
        source: data.source,
        sourceDetail: data.source_detail,
        customerName: data.customer_name,
        customerEmail: data.customer_email,
        customerPhone: data.customer_phone,
        companyName: data.company_name,
        serviceType: data.service_type,
        pickupAddress: data.pickup_address,
        pickupDatetime: data.pickup_datetime,
        dropoffAddress: data.dropoff_address,
        estimatedValue: data.estimated_value,
        score: data.score,
        scoreBreakdown: data.score_breakdown,
        assignedTo: data.assigned_to,
        assignedAt: data.assigned_at,
        status: data.status,
        lostReason: data.lost_reason,
        outcomeNotes: data.outcome_notes,
        nextFollowUpAt: data.next_follow_up_at,
        lastContactedAt: data.last_contacted_at,
        firstContactedAt: data.first_contacted_at,
        contactCount: data.contact_count,
        bookingId: data.booking_id,
        convertedAt: data.converted_at,
        tags: data.tags || [],
        notes: data.notes,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        stage: data.lead_stages ? {
          id: (data.lead_stages as any).id,
          name: (data.lead_stages as any).name,
          color: (data.lead_stages as any).color,
          position: (data.lead_stages as any).position,
          isDefault: (data.lead_stages as any).is_default,
          isWon: (data.lead_stages as any).is_won,
          isLost: (data.lead_stages as any).is_lost,
        } : undefined,
        assignee: data.profiles ? {
          id: (data.profiles as any).id,
          fullName: (data.profiles as any).full_name,
          email: (data.profiles as any).email,
        } : undefined,
        quote: data.booking_quotes ? {
          id: (data.booking_quotes as any).id,
          token: (data.booking_quotes as any).token,
          status: (data.booking_quotes as any).status,
          totalAmount: (data.booking_quotes as any).total_amount,
        } : undefined,
      }
    },
    enabled: !!leadId,
    staleTime: 10 * 1000,
  })
}

/**
 * Admin-specific hook to fetch lead activities without account context
 */
function useAdminLeadActivities(leadId: string | undefined) {
  return useQuery({
    queryKey: ['admin', 'lead-activities', leadId],
    queryFn: async () => {
      if (!leadId) return []

      const { data, error } = await supabase
        .from('lead_activities')
        .select(`
          *,
          profiles:performed_by (
            id,
            full_name,
            email
          )
        `)
        .eq('lead_id', leadId)
        .order('created_at', { ascending: false })

      if (error) throw error

      return (data || []).map((activity: any) => ({
        id: activity.id,
        leadId: activity.lead_id,
        activityType: activity.activity_type,
        title: activity.title,
        description: activity.description,
        oldValue: activity.old_value,
        newValue: activity.new_value,
        performedBy: activity.performed_by,
        performer: activity.profiles ? {
          id: activity.profiles.id,
          fullName: activity.profiles.full_name,
          email: activity.profiles.email,
        } : undefined,
        createdAt: activity.created_at,
      }))
    },
    enabled: !!leadId,
    staleTime: 10 * 1000,
  })
}

/**
 * Admin-specific hook to fetch lead follow-ups without account context
 */
function useAdminLeadFollowUps(leadId: string | undefined) {
  return useQuery({
    queryKey: ['admin', 'lead-follow-ups', leadId],
    queryFn: async () => {
      if (!leadId) return []

      const { data, error } = await supabase
        .from('lead_follow_ups')
        .select(`
          *,
          profiles:assigned_to (
            id,
            full_name,
            email
          )
        `)
        .eq('lead_id', leadId)
        .order('due_at', { ascending: true })

      if (error) throw error

      return (data || []).map((followUp: any) => ({
        id: followUp.id,
        leadId: followUp.lead_id,
        title: followUp.title,
        description: followUp.description,
        dueAt: followUp.due_at,
        assignedTo: followUp.assigned_to,
        assignee: followUp.profiles ? {
          id: followUp.profiles.id,
          fullName: followUp.profiles.full_name,
          email: followUp.profiles.email,
        } : undefined,
        status: followUp.status,
        completedAt: followUp.completed_at,
        createdAt: followUp.created_at,
      }))
    },
    enabled: !!leadId,
    staleTime: 10 * 1000,
  })
}

/**
 * Admin-specific hook to fetch lead stages for the account
 */
function useAdminLeadStages(accountId: string | undefined) {
  return useQuery({
    queryKey: ['admin', 'lead-stages', accountId],
    queryFn: async () => {
      if (!accountId) return []
      const { data, error } = await supabase
        .from('lead_stages')
        .select('*')
        .eq('account_id', accountId)
        .order('position', { ascending: true })

      if (error) throw error

      return (data || []).map((stage: any) => ({
        id: stage.id,
        name: stage.name,
        color: stage.color,
        position: stage.position,
        isDefault: stage.is_default,
        isWon: stage.is_won,
        isLost: stage.is_lost,
      }))
    },
    enabled: !!accountId,
    staleTime: 60 * 1000,
  })
}

/**
 * Admin-specific mutation to update a lead (no account context needed)
 */
function useAdminUpdateLead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ leadId, ...request }: {
      leadId: string
      stageId?: string
      status?: string
      lostReason?: string
      outcomeNotes?: string
    }) => {
      const updateData: Record<string, any> = {}

      if (request.stageId !== undefined) updateData.stage_id = request.stageId
      if (request.status !== undefined) updateData.status = request.status
      if (request.lostReason !== undefined) updateData.lost_reason = request.lostReason
      if (request.outcomeNotes !== undefined) updateData.outcome_notes = request.outcomeNotes

      const { data, error } = await supabase
        .from('leads')
        .update(updateData)
        .eq('id', leadId)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'lead', variables.leadId] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'leads'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'lead-activities', variables.leadId] })
    },
  })
}

/**
 * Admin-specific mutation to create an activity on a lead
 */
function useAdminCreateActivity() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      leadId: string
      activityType: string
      title: string
      description?: string
    }) => {
      const { data: { user } } = await supabase.auth.getUser()

      const { data, error } = await supabase
        .from('lead_activities')
        .insert({
          lead_id: request.leadId,
          activity_type: request.activityType,
          title: request.title,
          description: request.description || null,
          performed_by: user?.id || null,
        })
        .select()
        .single()

      if (error) throw error

      // Auto-stage: move to "Contacted" or "Follow Up" from any non-terminal stage
      if (['call', 'email', 'meeting'].includes(request.activityType)) {
        try {
          const { data: lead } = await supabase
            .from('leads')
            .select('id, account_id, stage_id, status, lead_stages!inner(name, is_won, is_lost)')
            .eq('id', request.leadId)
            .single()

          const currentStage = (lead as any)?.lead_stages
          const currentStageName = currentStage?.name

          if (
            lead?.status === 'open' &&
            currentStageName !== 'Contacted' &&
            !currentStage?.is_won &&
            !currentStage?.is_lost
          ) {
            const { count: pendingCount } = await supabase
              .from('lead_follow_ups')
              .select('*', { count: 'exact', head: true })
              .eq('lead_id', request.leadId)
              .eq('status', 'pending')

            if (pendingCount && pendingCount > 0 && currentStageName === 'Follow Up') {
              // Stage is locked -- do nothing
            } else {
              const targetStageName = (pendingCount && pendingCount > 0) ? 'Follow Up' : 'Contacted'

              const { data: targetStage } = await supabase
                .from('lead_stages')
                .select('id')
                .eq('account_id', lead.account_id)
                .eq('name', targetStageName)
                .single()

              if (targetStage) {
                await supabase
                  .from('leads')
                  .update({ stage_id: targetStage.id })
                  .eq('id', request.leadId)
              }
            }
          }
        } catch {
          // Don't fail the activity creation if auto-stage fails
        }
      }

      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'lead', variables.leadId] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'lead-activities', variables.leadId] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'leads'] })
    },
  })
}

/**
 * Admin-specific mutation to create a follow-up on a lead
 */
function useAdminCreateFollowUp() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      leadId: string
      title: string
      description?: string
      dueAt: string
    }) => {
      const { data: { user } } = await supabase.auth.getUser()

      const { data, error } = await supabase
        .from('lead_follow_ups')
        .insert({
          lead_id: request.leadId,
          title: request.title,
          description: request.description || null,
          due_at: request.dueAt,
          assigned_to: user?.id || null,
          status: 'pending',
        })
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'lead', variables.leadId] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'lead-follow-ups', variables.leadId] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'lead-activities', variables.leadId] })
    },
  })
}

/**
 * Admin-specific mutation to update a follow-up (e.g. complete it)
 */
function useAdminUpdateFollowUp() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      followUpId: string
      leadId: string
      status: string
    }) => {
      const updateData: Record<string, any> = { status: request.status }
      if (request.status === 'completed') {
        updateData.completed_at = new Date().toISOString()
      }

      const { data, error } = await supabase
        .from('lead_follow_ups')
        .update(updateData)
        .eq('id', request.followUpId)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'lead', variables.leadId] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'lead-follow-ups', variables.leadId] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'lead-activities', variables.leadId] })
    },
  })
}

/**
 * Admin-specific mutation to delete an activity
 */
function useAdminDeleteActivity() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ activityId }: { activityId: string; leadId: string }) => {
      const { error } = await supabase
        .from('lead_activities')
        .delete()
        .eq('id', activityId)

      if (error) throw error
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'lead-activities', variables.leadId] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'lead', variables.leadId] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'leads'] })
    },
  })
}

/**
 * Admin-specific mutation to delete a follow-up
 */
function useAdminDeleteFollowUp() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ followUpId }: { followUpId: string; leadId: string }) => {
      const { error } = await supabase
        .from('lead_follow_ups')
        .delete()
        .eq('id', followUpId)

      if (error) throw error
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'lead-follow-ups', variables.leadId] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'lead', variables.leadId] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'leads'] })
    },
  })
}

/**
 * Admin-specific mutation to delete a lead
 */
function useAdminDeleteLead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ leadId }: { leadId: string }) => {
      const { error } = await supabase
        .from('leads')
        .delete()
        .eq('id', leadId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'leads'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'lead-stats'] })
    },
  })
}

/**
 * Admin-specific hook to fetch open leads for next-lead navigation
 */
function useAdminOpenLeads() {
  return useQuery({
    queryKey: ['admin', 'leads', 'open'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('leads')
        .select('id')
        .eq('status', 'open')
        .order('created_at', { ascending: false })
        .limit(100)

      if (error) throw error
      return data || []
    },
    staleTime: 10 * 1000,
  })
}

export function AdminLeadDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { startImpersonation } = useImpersonation()

  // Fetch data
  const { data: lead, isLoading, error } = useAdminLead(id)
  const { data: stages } = useAdminLeadStages(lead?.accountId)
  const { data: activities, isLoading: activitiesLoading } = useAdminLeadActivities(id)
  const { data: followUps, isLoading: followUpsLoading } = useAdminLeadFollowUps(id)
  const { data: openLeads } = useAdminOpenLeads()

  // Mutations
  const updateLead = useAdminUpdateLead()
  const createActivity = useAdminCreateActivity()
  const createFollowUp = useAdminCreateFollowUp()
  const updateFollowUp = useAdminUpdateFollowUp()
  const deleteActivity = useAdminDeleteActivity()
  const deleteFollowUp = useAdminDeleteFollowUp()
  const deleteLead = useAdminDeleteLead()

  // Dialog states
  const [wonDialogOpen, setWonDialogOpen] = useState(false)
  const [lostDialogOpen, setLostDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [activityDialogOpen, setActivityDialogOpen] = useState(false)
  const [followUpDialogOpen, setFollowUpDialogOpen] = useState(false)

  // Form states
  const [activityType, setActivityType] = useState<'note' | 'call' | 'email' | 'meeting'>('note')
  const [activityTitle, setActivityTitle] = useState('')
  const [activityDescription, setActivityDescription] = useState('')
  const [followUpTitle, setFollowUpTitle] = useState('')
  const [followUpDescription, setFollowUpDescription] = useState('')
  const [followUpDueAt, setFollowUpDueAt] = useState(getDefaultFollowUpDate)

  // Filter active stages for pipeline display (exclude Won/Lost)
  const activeStages = stages?.filter(s => !s.isWon && !s.isLost) || []

  // Merge activities and follow-ups into a unified timeline
  type TimelineItem =
    | { type: 'activity'; data: NonNullable<typeof activities>[number] }
    | { type: 'follow_up'; data: NonNullable<typeof followUps>[number] }

  const timelineItems: TimelineItem[] = useMemo(() => {
    const items: TimelineItem[] = [
      ...(activities || []).map(a => ({ type: 'activity' as const, data: a })),
      ...(followUps || []).map(f => ({ type: 'follow_up' as const, data: f })),
    ]
    return items.sort((a, b) => {
      const dateA = new Date(a.type === 'activity' ? a.data.createdAt : a.data.createdAt).getTime()
      const dateB = new Date(b.type === 'activity' ? b.data.createdAt : b.data.createdAt).getTime()
      return dateB - dateA
    })
  }, [activities, followUps])

  // Get next lead ID for navigation after marking won/lost
  const getNextLeadId = (): string | null => {
    if (!openLeads || !id) return null
    const currentIndex = openLeads.findIndex(l => l.id === id)
    if (currentIndex === -1) return openLeads[0]?.id || null
    const nextLead = openLeads[currentIndex + 1] || openLeads[currentIndex - 1] || openLeads[0]
    return nextLead?.id !== id ? nextLead?.id || null : null
  }

  // Handle mark as won
  const handleMarkWon = async (data: { outcomeNotes?: string }) => {
    if (!lead) return

    const wonStage = stages?.find(s => s.isWon)
    const nextLeadId = getNextLeadId()

    try {
      await updateLead.mutateAsync({
        leadId: lead.id,
        status: 'won',
        stageId: wonStage?.id,
        outcomeNotes: data.outcomeNotes,
      })
      setWonDialogOpen(false)
      toast({
        title: 'Lead won!',
        description: 'Congratulations on converting this lead!',
      })
      if (nextLeadId) {
        navigate(`/admin/leads/${nextLeadId}`)
      } else {
        navigate('/admin/leads')
      }
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to mark lead as won',
        variant: 'destructive',
      })
    }
  }

  // Handle mark as lost
  const handleMarkLost = async (data: { outcomeNotes?: string; lostReason?: string }) => {
    if (!lead || !data.lostReason) return

    const lostStage = stages?.find(s => s.isLost)
    const nextLeadId = getNextLeadId()

    try {
      await updateLead.mutateAsync({
        leadId: lead.id,
        status: 'lost',
        lostReason: data.lostReason,
        outcomeNotes: data.outcomeNotes,
        stageId: lostStage?.id,
      })
      setLostDialogOpen(false)
      toast({
        title: 'Lead marked as lost',
        description: 'The lead has been closed.',
      })
      if (nextLeadId) {
        navigate(`/admin/leads/${nextLeadId}`)
      } else {
        navigate('/admin/leads')
      }
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to mark lead as lost',
        variant: 'destructive',
      })
    }
  }

  // Handle delete lead
  const handleDeleteLead = async () => {
    if (!lead) return

    try {
      await deleteLead.mutateAsync({ leadId: lead.id })
      setDeleteDialogOpen(false)
      toast({
        title: 'Lead deleted',
        description: 'The lead and all related data have been permanently deleted.',
      })
      navigate('/admin/leads')
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to delete lead',
        variant: 'destructive',
      })
    }
  }

  // Handle stage change
  const handleStageChange = async (stageId: string) => {
    if (!lead) return

    const stage = stages?.find(s => s.id === stageId)
    if (stage?.isWon) {
      setWonDialogOpen(true)
      return
    }
    if (stage?.isLost) {
      setLostDialogOpen(true)
      return
    }

    try {
      await updateLead.mutateAsync({ leadId: lead.id, stageId })
      toast({
        title: 'Stage updated',
        description: `Lead moved to "${stage?.name || 'new stage'}"`,
      })
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to update stage',
        variant: 'destructive',
      })
    }
  }

  // Handle create activity
  const handleCreateActivity = async () => {
    if (!lead || !activityTitle) return

    try {
      await createActivity.mutateAsync({
        leadId: lead.id,
        activityType,
        title: activityTitle,
        description: activityDescription || undefined,
      })
      setActivityDialogOpen(false)
      setActivityTitle('')
      setActivityDescription('')
      toast({
        title: 'Activity logged',
        description: 'Your activity has been recorded.',
      })
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to log activity',
        variant: 'destructive',
      })
    }
  }

  // Handle create follow-up
  const handleCreateFollowUp = async () => {
    if (!lead || !followUpTitle || !followUpDueAt) return

    try {
      await createFollowUp.mutateAsync({
        leadId: lead.id,
        title: followUpTitle,
        description: followUpDescription || undefined,
        dueAt: new Date(followUpDueAt).toISOString(),
      })
      setFollowUpDialogOpen(false)
      setFollowUpTitle('')
      setFollowUpDescription('')
      setFollowUpDueAt(getDefaultFollowUpDate())
      toast({
        title: 'Follow-up scheduled',
        description: 'Your follow-up has been created.',
      })
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to schedule follow-up',
        variant: 'destructive',
      })
    }
  }

  // Handle complete follow-up
  const handleCompleteFollowUp = async (followUpId: string) => {
    if (!lead) return

    try {
      await updateFollowUp.mutateAsync({
        followUpId,
        leadId: lead.id,
        status: 'completed',
      })
      toast({
        title: 'Follow-up completed',
        description: 'The follow-up has been marked as done.',
      })
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to complete follow-up',
        variant: 'destructive',
      })
    }
  }

  if (!id) return null

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-start gap-4">
          <Skeleton className="h-10 w-10" />
          <div>
            <Skeleton className="h-8 w-48" />
            <div className="mt-2 flex gap-2">
              <Skeleton className="h-5 w-20" />
              <Skeleton className="h-5 w-16" />
            </div>
          </div>
        </div>
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <Card><CardContent className="p-6"><Skeleton className="h-32" /></CardContent></Card>
            <Card><CardContent className="p-6"><Skeleton className="h-64" /></CardContent></Card>
          </div>
          <div className="space-y-6">
            <Card><CardContent className="p-6"><Skeleton className="h-48" /></CardContent></Card>
            <Card><CardContent className="p-6"><Skeleton className="h-32" /></CardContent></Card>
          </div>
        </div>
      </div>
    )
  }

  if (error || !lead) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
        <p className="text-lg font-medium mb-2">Lead not found</p>
        <p className="text-muted-foreground mb-4">
          The lead you&apos;re looking for doesn&apos;t exist or has been deleted.
        </p>
        <Button variant="outline" onClick={() => navigate('/admin/leads')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Leads
        </Button>
      </div>
    )
  }

  const pendingFollowUps = followUps?.filter(f => f.status === 'pending') || []
  const completedFollowUps = followUps?.filter(f => f.status === 'completed') || []

  const deletableActivityTypes = ['note', 'call', 'email', 'meeting', 'follow_up', 'quote_updated']

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/leads')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            {/* Breadcrumb */}
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-1">
              <span
                className="hover:text-foreground cursor-pointer"
                onClick={() => navigate('/admin')}
              >
                Admin
              </span>
              <span>/</span>
              <span
                className="hover:text-foreground cursor-pointer"
                onClick={() => navigate('/admin/leads')}
              >
                Leads
              </span>
              <span>/</span>
              <span className="text-foreground">{lead.customerName}</span>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight">{lead.customerName}</h1>
              <LeadScoreBadge score={lead.score} breakdown={lead.scoreBreakdown} />
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <LeadStatusBadge status={lead.status} />
              <LeadSourceBadge source={lead.source} sourceDetail={lead.sourceDetail} />
              <LeadAgeIndicator createdAt={lead.createdAt} firstContactedAt={lead.firstContactedAt} size="sm" />
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          {/* Quick Actions */}
          {lead.customerPhone && (
            <Button variant="outline" size="sm" asChild>
              <a href={`tel:${lead.customerPhone}`}>
                <Phone className="mr-2 h-4 w-4" />
                Call
              </a>
            </Button>
          )}
          {lead.customerEmail && (
            <Button variant="outline" size="sm" asChild>
              <a href={`mailto:${lead.customerEmail}`}>
                <Mail className="mr-2 h-4 w-4" />
                Email
              </a>
            </Button>
          )}
          {lead.status === 'open' && (
            <>
              <Button
                variant="default"
                size="sm"
                className="bg-green-600 hover:bg-green-700"
                onClick={() => setWonDialogOpen(true)}
              >
                <Trophy className="mr-2 h-4 w-4" />
                Mark Won
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setLostDialogOpen(true)}
              >
                <ThumbsDown className="mr-2 h-4 w-4" />
                Mark Lost
              </Button>
            </>
          )}
          <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" className="text-destructive hover:text-destructive">
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this lead?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete <strong>{lead.customerName}</strong> and all related activities, follow-ups, and notes. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={handleDeleteLead}
                  disabled={deleteLead.isPending}
                >
                  {deleteLead.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    'Delete Lead'
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left Column - Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Account Info Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Account
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="text-sm">
                  {lead.accountName}
                </Badge>
                {lead.accountStatus !== 'active' && (
                  <Badge variant="destructive" className="text-xs">
                    {lead.accountStatus}
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Pipeline Stage */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Pipeline Stage</CardTitle>
              {pendingFollowUps.length > 0 && lead.status === 'open' && (
                <p className="text-xs text-amber-600 flex items-center gap-1 mt-1">
                  <Lock className="h-3 w-3" />
                  Stage locked while follow-up is scheduled
                </p>
              )}
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {activeStages.map((stage) => {
                  const isLocked = pendingFollowUps.length > 0 && lead.status === 'open'
                  return (
                    <button
                      key={stage.id}
                      onClick={() => handleStageChange(stage.id)}
                      disabled={lead.status !== 'open' || updateLead.isPending || isLocked}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                        lead.stageId === stage.id
                          ? 'ring-2 ring-offset-2'
                          : 'opacity-60 hover:opacity-100'
                      } ${lead.status !== 'open' || isLocked ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'}`}
                      style={{
                        backgroundColor: `${stage.color}20`,
                        borderColor: `${stage.color}60`,
                        color: stage.color,
                        ...(lead.stageId === stage.id ? { ringColor: stage.color } : {}),
                      }}
                    >
                      {stage.name}
                    </button>
                  )
                })}
              </div>
            </CardContent>
          </Card>

          {/* Contact & Trip Details */}
          <Card>
            <CardHeader>
              <CardTitle>Lead Details</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-6 sm:grid-cols-2">
              {/* Contact Info */}
              <div className="space-y-4">
                <h4 className="font-medium text-sm text-muted-foreground">Contact Information</h4>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <User className="h-4 w-4 mt-1 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Name</p>
                      <p className="font-medium">{lead.customerName}</p>
                    </div>
                  </div>
                  {lead.customerEmail && (
                    <div className="flex items-start gap-3">
                      <Mail className="h-4 w-4 mt-1 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Email</p>
                        <a href={`mailto:${lead.customerEmail}`} className="font-medium text-primary hover:underline">
                          {lead.customerEmail}
                        </a>
                      </div>
                    </div>
                  )}
                  {lead.customerPhone && (
                    <div className="flex items-start gap-3">
                      <Phone className="h-4 w-4 mt-1 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Phone</p>
                        <a href={`tel:${lead.customerPhone}`} className="font-medium text-primary hover:underline">
                          {lead.customerPhone}
                        </a>
                      </div>
                    </div>
                  )}
                  {lead.companyName && (
                    <div className="flex items-start gap-3">
                      <Building className="h-4 w-4 mt-1 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Company</p>
                        <p className="font-medium">{lead.companyName}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Trip Info */}
              <div className="space-y-4">
                <h4 className="font-medium text-sm text-muted-foreground">Trip Details</h4>
                <div className="space-y-3">
                  {lead.serviceType && (
                    <div className="flex items-start gap-3">
                      <FileText className="h-4 w-4 mt-1 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Service Type</p>
                        <p className="font-medium">
                          {lead.serviceType === 'point_to_point' ? 'Point to Point' : 'Hourly'}
                        </p>
                      </div>
                    </div>
                  )}
                  {lead.pickupDatetime && (
                    <div className="flex items-start gap-3">
                      <Calendar className="h-4 w-4 mt-1 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Pickup Date</p>
                        <p className="font-medium">{formatDateTime(lead.pickupDatetime)}</p>
                      </div>
                    </div>
                  )}
                  {lead.pickupAddress && (
                    <div className="flex items-start gap-3">
                      <MapPin className="h-4 w-4 mt-1 text-green-600" />
                      <div>
                        <p className="text-sm text-muted-foreground">Pickup</p>
                        <p className="font-medium text-sm">{lead.pickupAddress}</p>
                      </div>
                    </div>
                  )}
                  {lead.dropoffAddress && (
                    <div className="flex items-start gap-3">
                      <MapPin className="h-4 w-4 mt-1 text-red-600" />
                      <div>
                        <p className="text-sm text-muted-foreground">Dropoff</p>
                        <p className="font-medium text-sm">{lead.dropoffAddress}</p>
                      </div>
                    </div>
                  )}
                  {lead.estimatedValue && (
                    <div className="flex items-start gap-3">
                      <Tag className="h-4 w-4 mt-1 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Estimated Value</p>
                        <p className="font-medium text-lg">{formatCurrency(lead.estimatedValue)}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Activity Timeline */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Activity Timeline</CardTitle>
                <CardDescription>History of interactions with this lead</CardDescription>
              </div>
              <Dialog open={activityDialogOpen} onOpenChange={setActivityDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm">
                    <StickyNote className="mr-2 h-4 w-4" />
                    Log Activity
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Log Activity</DialogTitle>
                    <DialogDescription>Record an interaction with this lead.</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Activity Type</Label>
                      <Select value={activityType} onValueChange={(v: any) => setActivityType(v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="note">Note</SelectItem>
                          <SelectItem value="call">Phone Call</SelectItem>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="meeting">Meeting</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Title</Label>
                      <Input
                        placeholder="Brief summary..."
                        value={activityTitle}
                        onChange={(e) => setActivityTitle(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description (optional)</Label>
                      <Textarea
                        placeholder="Add more details..."
                        value={activityDescription}
                        onChange={(e) => setActivityDescription(e.target.value)}
                        rows={3}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setActivityDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleCreateActivity} disabled={!activityTitle || createActivity.isPending}>
                      {createActivity.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Log Activity
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {(activitiesLoading || followUpsLoading) ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex gap-3">
                      <Skeleton className="h-8 w-8 rounded-full" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-3 w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : timelineItems.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No activity recorded yet.
                </p>
              ) : (
                <div className="relative space-y-4">
                  <div className="absolute left-4 top-0 bottom-0 w-px bg-border" />

                  {timelineItems.map((item) => {
                    if (item.type === 'follow_up') {
                      const followUp = item.data
                      const isPending = followUp.status === 'pending'
                      const isCompleted = followUp.status === 'completed'
                      const isCancelled = followUp.status === 'cancelled'
                      const isOverdue = isPending && new Date(followUp.dueAt) < new Date()

                      return (
                        <div key={`fu-${followUp.id}`} className="relative flex gap-4 pl-10 group">
                          <div className={`absolute left-0 p-1 rounded-full bg-background border ${
                            isPending ? (isOverdue ? 'border-red-300' : 'border-amber-300') :
                            isCompleted ? 'border-green-300' : 'border-gray-300'
                          }`}>
                            {isPending ? (
                              <CalendarClock className={`h-4 w-4 ${isOverdue ? 'text-red-500' : 'text-amber-500'}`} />
                            ) : isCompleted ? (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            ) : (
                              <XCircle className="h-4 w-4 text-gray-400" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-sm">{followUp.title}</p>
                                  <Badge variant={isPending ? (isOverdue ? 'destructive' : 'outline') : 'secondary'}
                                    className="text-xs px-1.5 py-0">
                                    {isPending ? (isOverdue ? 'Overdue' : 'Scheduled') : isCompleted ? 'Completed' : 'Cancelled'}
                                  </Badge>
                                </div>
                                <p className={`text-xs mt-0.5 ${isOverdue ? 'text-red-600' : 'text-muted-foreground'}`}>
                                  {isPending ? 'Due' : isCompleted ? 'Completed' : 'Cancelled'}: {formatDateTime(isPending ? followUp.dueAt : (followUp.completedAt || followUp.dueAt))}
                                </p>
                                {followUp.description && (
                                  <p className="text-sm text-muted-foreground mt-1">{followUp.description}</p>
                                )}
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-muted-foreground whitespace-nowrap">
                                  {formatRelativeTime(followUp.createdAt)}
                                </span>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <button className="p-1 rounded hover:bg-muted opacity-0 group-hover:opacity-100 transition-opacity">
                                      <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                                    </button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Delete follow-up?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        This will permanently delete this follow-up. This cannot be undone.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                                      <AlertDialogAction
                                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                        onClick={() => {
                                          deleteFollowUp.mutate(
                                            { followUpId: followUp.id, leadId: lead.id },
                                            {
                                              onSuccess: () => toast({ title: 'Follow-up deleted' }),
                                              onError: () => toast({ title: 'Error', description: 'Failed to delete follow-up', variant: 'destructive' }),
                                            }
                                          )
                                        }}
                                      >
                                        Delete
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    }

                    const activity = item.data
                    const Icon = activityIcons[activity.activityType] || FileText
                    const canDelete = deletableActivityTypes.includes(activity.activityType)

                    return (
                      <div key={`act-${activity.id}`} className="relative flex gap-4 pl-10 group">
                        <div className="absolute left-0 p-1 rounded-full bg-background border">
                          <Icon className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="font-medium text-sm">{activity.title}</p>
                              {activity.description && (
                                <p className="text-sm text-muted-foreground mt-1">
                                  {activity.description}
                                </p>
                              )}
                              {activity.oldValue && activity.newValue && (
                                <p className="text-sm text-muted-foreground mt-1">
                                  {activity.oldValue} &rarr; {activity.newValue}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-muted-foreground whitespace-nowrap">
                                {formatRelativeTime(activity.createdAt)}
                              </span>
                              {canDelete && (
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <button className="p-1 rounded hover:bg-muted opacity-0 group-hover:opacity-100 transition-opacity">
                                      <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                                    </button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Delete activity?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        This will permanently delete this activity. This cannot be undone.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                                      <AlertDialogAction
                                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                        onClick={() => {
                                          deleteActivity.mutate(
                                            { activityId: activity.id, leadId: lead.id },
                                            {
                                              onSuccess: () => toast({ title: 'Activity deleted' }),
                                              onError: () => toast({ title: 'Error', description: 'Failed to delete activity', variant: 'destructive' }),
                                            }
                                          )
                                        }}
                                      >
                                        Delete
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              )}
                            </div>
                          </div>
                          {activity.performer && (
                            <p className="text-xs text-muted-foreground mt-1">
                              by {activity.performer.fullName || activity.performer.email}
                            </p>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Sidebar */}
        <div className="space-y-6">
          {/* Linked Quote */}
          {lead.quoteId && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Linked Quote</CardTitle>
              </CardHeader>
              <CardContent>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    startImpersonation(lead.accountSlug)
                    navigate(`/admin/accounts/${lead.accountSlug}/view/quotes/${lead.quoteId}`)
                  }}
                >
                  <FileText className="mr-2 h-4 w-4" />
                  View Quote
                  <ExternalLink className="ml-2 h-3 w-3" />
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Follow-ups */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="text-base">Follow-ups</CardTitle>
              <Dialog open={followUpDialogOpen} onOpenChange={setFollowUpDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline">
                    <CalendarPlus className="mr-2 h-4 w-4" />
                    Schedule
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Schedule Follow-up</DialogTitle>
                    <DialogDescription>Set a reminder to follow up with this lead.</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Title</Label>
                      <Input
                        placeholder="e.g., Call to discuss pricing"
                        value={followUpTitle}
                        onChange={(e) => setFollowUpTitle(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Due Date & Time</Label>
                      <Input
                        type="datetime-local"
                        value={followUpDueAt}
                        onChange={(e) => setFollowUpDueAt(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Notes (optional)</Label>
                      <Textarea
                        placeholder="Add notes..."
                        value={followUpDescription}
                        onChange={(e) => setFollowUpDescription(e.target.value)}
                        rows={2}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setFollowUpDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button
                      onClick={handleCreateFollowUp}
                      disabled={!followUpTitle || !followUpDueAt || createFollowUp.isPending}
                    >
                      {createFollowUp.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Schedule
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {followUpsLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              ) : pendingFollowUps.length === 0 ? (
                <p className="text-muted-foreground text-sm text-center py-4">
                  No follow-ups scheduled
                </p>
              ) : (
                <div className="space-y-2">
                  {pendingFollowUps.map((followUp) => {
                    const isOverdue = new Date(followUp.dueAt) < new Date()
                    return (
                      <div
                        key={followUp.id}
                        className={`p-3 rounded-lg border ${isOverdue ? 'border-red-200 bg-red-50' : ''}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate">{followUp.title}</p>
                            <p className={`text-xs ${isOverdue ? 'text-red-600' : 'text-muted-foreground'}`}>
                              {isOverdue ? 'Overdue: ' : 'Due: '}
                              {formatDateTime(followUp.dueAt)}
                            </p>
                            {followUp.assignee && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Assigned to: {followUp.assignee.fullName || followUp.assignee.email}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8"
                              onClick={() => handleCompleteFollowUp(followUp.id)}
                              disabled={updateFollowUp.isPending}
                            >
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="icon" variant="ghost" className="h-8 w-8">
                                  <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete follow-up?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will permanently delete this follow-up. This cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    onClick={() => {
                                      deleteFollowUp.mutate(
                                        { followUpId: followUp.id, leadId: lead.id },
                                        {
                                          onSuccess: () => toast({ title: 'Follow-up deleted' }),
                                          onError: () => toast({ title: 'Error', description: 'Failed to delete follow-up', variant: 'destructive' }),
                                        }
                                      )
                                    }}
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
              {completedFollowUps.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-xs text-muted-foreground mb-2">
                    {completedFollowUps.length} completed
                  </p>
                  <div className="space-y-1">
                    {completedFollowUps.slice(0, 3).map((followUp) => (
                      <div key={followUp.id} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="h-3 w-3 text-green-500" />
                        <span className="truncate">{followUp.title}</span>
                      </div>
                    ))}
                    {completedFollowUps.length > 3 && (
                      <p className="text-xs text-muted-foreground">
                        +{completedFollowUps.length - 3} more
                      </p>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          {lead.notes && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm whitespace-pre-wrap">{lead.notes}</p>
              </CardContent>
            </Card>
          )}

          {/* Tags */}
          {lead.tags && lead.tags.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Tags</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {lead.tags.map((tag: string) => (
                    <Badge key={tag} variant="secondary">{tag}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Lost Reason */}
          {lead.status === 'lost' && lead.lostReason && (
            <Card className="border-red-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-red-600">Lost Reason</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">{lead.lostReason}</p>
                {lead.outcomeNotes && (
                  <p className="text-sm text-muted-foreground mt-2 italic">
                    {lead.outcomeNotes}
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Won Notes */}
          {lead.status === 'won' && lead.outcomeNotes && (
            <Card className="border-green-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-green-600">Win Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">{lead.outcomeNotes}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Won Dialog */}
      <LeadOutcomeDialog
        open={wonDialogOpen}
        onOpenChange={setWonDialogOpen}
        leadName={lead.customerName}
        outcome="won"
        onConfirm={handleMarkWon}
        isLoading={updateLead.isPending}
      />

      {/* Lost Dialog */}
      <LeadOutcomeDialog
        open={lostDialogOpen}
        onOpenChange={setLostDialogOpen}
        leadName={lead.customerName}
        outcome="lost"
        onConfirm={handleMarkLost}
        isLoading={updateLead.isPending}
      />
    </div>
  )
}
